// src/hooks/useWatchdog.ts
import { useEffect, useRef, useState } from "react";
import {
  DEV_HOOKS,
  WD_CHECK_INTERVAL_MS,
  WD_MAX_RECONNECTS_PER_SESSION,
  WD_MIN_SESSION_MS,
  WD_NO_FINAL_MS,
  WD_NO_PARTIAL_MS,
} from "../config/live";

type UseWatchdogArgs = {
  armed: boolean; // user intent to keep listening
  onSoftRestart: () => Promise<void>; // provided by container (no audio imports in this hook)
  setUiActive: (active: boolean) => void; // Live's setter to keep UI "active" after restart
};

export function useWatchdog({ armed, onSoftRestart, setUiActive }: UseWatchdogArgs) {
  // ages & counters
  const sessionStartMsRef = useRef<number>(0);
  const lastPartialMsRef = useRef<number>(0);
  const lastFinalMsRef = useRef<number>(0);
  const reconnectCountRef = useRef<number>(0);

  // flags & timers
  // HARD-CODED: Watchdog is always enabled (no localStorage flag).
  const watchdogEnabledRef = useRef<boolean>(true);
  const watchdogTimerRef = useRef<number | null>(null);
  const preemptTimerRef = useRef<number | null>(null);
  const wdInFlightRef = useRef<boolean>(false);
  const wdForceNextRef = useRef<boolean>(false);

  // UI helpers
  const [listeningSinceMs, setListeningSinceMs] = useState<number>(0);
  const [lastHeardAgoSec, setLastHeardAgoSec] = useState<number | null>(null);

  // keep a local second-ticker to update "last heard" badge
  useEffect(() => {
    const id = window.setInterval(() => {
      if (!armed) { setLastHeardAgoSec(null); return; }
      const now = Date.now();
      const last = Math.max(lastPartialMsRef.current, lastFinalMsRef.current);
      setLastHeardAgoSec(Math.floor((now - last) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [armed]);

  // core single check
  const wdCheckOnce = async () => {
    try {
      if (!watchdogEnabledRef.current) return;
      if (wdInFlightRef.current) return;
      wdInFlightRef.current = true;

      if (!armed) { wdInFlightRef.current = false; return; }

      const now = Date.now();
      const sessionAge = now - sessionStartMsRef.current;
      const noPartialFor = now - lastPartialMsRef.current;
      const noFinalFor = now - lastFinalMsRef.current;

      const forced = wdForceNextRef.current === true;

      if (!forced) {
        if (sessionAge < WD_MIN_SESSION_MS) { wdInFlightRef.current = false; return; }
        if (noPartialFor < WD_NO_PARTIAL_MS) { wdInFlightRef.current = false; return; }
        if (noFinalFor < WD_NO_FINAL_MS) { wdInFlightRef.current = false; return; }
      }

      if (reconnectCountRef.current >= WD_MAX_RECONNECTS_PER_SESSION) { wdInFlightRef.current = false; return; }

      reconnectCountRef.current += 1;
      console.log(
        `[STT WATCHDOG] ${forced ? "Forced " : ""}Auto-refresh #${reconnectCountRef.current} | ` +
        `age=${Math.round(sessionAge/1000)}s, noPartial=${noPartialFor}ms, noFinal=${noFinalFor}ms`
      );

      // reset one-shot force
      wdForceNextRef.current = false;

      // socket-only soft restart (capture reused; no picker)
      try {
        await onSoftRestart();
      } catch (err) {
        console.warn("[STT WATCHDOG] soft restart failed", err);
        wdInFlightRef.current = false;
        return;
      }

      const t = Date.now();
      sessionStartMsRef.current = t;
      lastPartialMsRef.current = t;
      lastFinalMsRef.current = t;
      setListeningSinceMs(t);

      // keep UI feeling “active”
      setUiActive(true);
      console.log("[STT WATCHDOG] System capture reinitialized via soft restart.");
    } catch (e) {
      console.warn("[STT WATCHDOG] refresh attempt failed", e);
    } finally {
      wdInFlightRef.current = false;
    }
  };

  const armWatchdogTimer = () => {
    if (!watchdogEnabledRef.current) return;
    if (watchdogTimerRef.current) {
      window.clearInterval(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    }
    console.log(
      `[STT WATCHDOG] ARMED | min=${WD_MIN_SESSION_MS}ms, check=${WD_CHECK_INTERVAL_MS}ms,` +
      ` noPartial>=${WD_NO_PARTIAL_MS}ms, noFinal>=${WD_NO_FINAL_MS}ms, max=${WD_MAX_RECONNECTS_PER_SESSION}`
    );
    watchdogTimerRef.current = window.setInterval(() => { void wdCheckOnce(); }, WD_CHECK_INTERVAL_MS);
  };

  const armPreemptTimer = () => {
    // Fire slightly BEFORE the 305s server cap → WD_MIN_SESSION_MS (~300s) minus a small cushion.
    if (preemptTimerRef.current) {
      window.clearTimeout(preemptTimerRef.current);
      preemptTimerRef.current = null;
    }
    const cushionMs = 1000; // 1s cushion to beat drift
    const delay = Math.max(0, WD_MIN_SESSION_MS - cushionMs);
    preemptTimerRef.current = window.setTimeout(() => {
      // one-shot forced pre-empt (ignore inactivity gates)
      wdForceNextRef.current = true;
      void wdCheckOnce();
    }, delay);
  };

  // public notifiers used by Live
  const notifyStreamStart = () => {
    const now = Date.now();
    sessionStartMsRef.current = now;
    lastPartialMsRef.current = now;
    lastFinalMsRef.current = now;
    reconnectCountRef.current = 0;
    setListeningSinceMs(now);

    // HARD-CODED ON
    watchdogEnabledRef.current = true;
    armWatchdogTimer();
    armPreemptTimer();
  };

  const notifyPartial = () => { lastPartialMsRef.current = Date.now(); };
  const notifyFinal = () => { lastFinalMsRef.current = Date.now(); };

  const notifyStreamEnd = () => {
    // Cancel pre-empt (session will be restarted imminently)
    if (preemptTimerRef.current) {
      window.clearTimeout(preemptTimerRef.current);
      preemptTimerRef.current = null;
    }
    // Force a restart immediately after a known end.
    wdForceNextRef.current = true;
    void wdCheckOnce();
  };

  // mount/unmount lifecycle
  useEffect(() => {
    // HARD-CODED ON at mount
    watchdogEnabledRef.current = true;
    armWatchdogTimer();
    return () => {
      if (watchdogTimerRef.current) {
        window.clearInterval(watchdogTimerRef.current);
        watchdogTimerRef.current = null;
      }
      if (preemptTimerRef.current) {
        window.clearTimeout(preemptTimerRef.current);
        preemptTimerRef.current = null;
      }
    };
  }, []);

  // optional DEV window hooks
  useEffect(() => {
    if (!DEV_HOOKS) return;
    const api = {
      wdForceOnce: () => { wdForceNextRef.current = true; return wdCheckOnce(); },
      wdSetAges: ({
        sessionMsAgo = WD_MIN_SESSION_MS + 1000,
        partialMsAgo = WD_NO_PARTIAL_MS + 1000,
        finalMsAgo = WD_NO_FINAL_MS + 1000,
      }: { sessionMsAgo?: number; partialMsAgo?: number; finalMsAgo?: number } = {}) => {
        const now = Date.now();
        sessionStartMsRef.current = now - sessionMsAgo;
        lastPartialMsRef.current = now - partialMsAgo;
        lastFinalMsRef.current = now - finalMsAgo;
        console.log("[STT WATCHDOG][DEV] Ages set", { sessionMsAgo, partialMsAgo, finalMsAgo });
      },
      wdStats: () => {
        const now = Date.now();
        const stats = {
          enabled: watchdogEnabledRef.current,
          sessionAge: now - sessionStartMsRef.current,
          noPartialFor: now - lastPartialMsRef.current,
          noFinalFor: now - lastFinalMsRef.current,
          reconnects: reconnectCountRef.current,
          maxReconnects: WD_MAX_RECONNECTS_PER_SESSION,
          minSessionMs: WD_MIN_SESSION_MS,
        };
        console.log("[STT WATCHDOG][DEV] Stats", stats);
        return stats;
      },
    };
    (window as any).cueboard = Object.assign((window as any).cueboard || {}, api);
    console.log("[STT WATCHDOG][DEV] Hooks enabled: window.cueboard.{wdForceOnce, wdSetAges, wdStats}");
    return () => {
      if ((window as any).cueboard) {
        delete (window as any).cueboard.wdForceOnce;
        delete (window as any).cueboard.wdSetAges;
        delete (window as any).cueboard.wdStats;
      }
    };
  }, []);

  return {
    listeningSinceMs,
    lastHeardAgoSec,
    notifyStreamStart,
    notifyPartial,
    notifyFinal,
    notifyStreamEnd,
  };
}
